import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

/* Utils */
import {
    useTheme,
    Text,
    Grid,
    GridItem,
    Box,
    Stack,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { FormattedMessage, useIntl } from 'react-intl';

import {useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

/* Components */
import StarRating from '../../components/pdp-reviews/partials/star-rating';
import FitSelector from '../../components/pdp-reviews/partials/fit-selector';
import Field from '../../components/field'

/* Fields */
import useWriteReviewFields from '../../components/forms/useWriteReviewFields'
import ImageUploader from  '../pdp-reviews/partials/image-uploader'
import { color } from '@chakra-ui/react';
import {useBrand} from '../../contexts/brand-context'

const WriteReviewFields = ({
    reviewFormData,
    form,
    prefix = '',
    children,
    product,
    onJoinNow
}) => {
    const theme = useTheme()
    const {isGarage} = useBrand()
    const {formatMessage} = useIntl()
    const [rating, setRating] = useState(0)
    const [fit, setFit] = useState([3])
    const [selectedSize, setSelectedSize] = useState('');
    const [selectedHeight, setSelectedHeight] = useState('');
    const [selectedWeight, setSelectedWeight] = useState('');
    const [reviewTextLength, setReviewTextLength] = useState(0)
    const fields = useWriteReviewFields({reviewFormData, form, prefix})

    const getSelectFieldStyling = ({ selectedValue, paddingLeft = '16px', color = 'black' }) => ({
        sx: {
            color: selectedValue ? 'black' : 'gray.600',
            'option': {
                color: color,
                'padding-left': paddingLeft
            },
            'option[value=""]': {
                color: 'gray.600'
            },
            'padding-inline-start': 0
        },
        fontSize: '14px',
        height: {base: '40px', lg: '34px'},
        lineHeight: '18px'
    });

    // Set the static value for the hidden input when the component mounts
    useEffect(() => {
        form.setValue(reviewFormData?.agreedtotermsandconditions?.Id, true);
    }, [form, reviewFormData]);

    const handleRatingChange = (newRating) => {
        setRating(newRating)
        form.setValue(reviewFormData?.rating?.Id, newRating);
        if (newRating > 0) {
            form.clearErrors(reviewFormData?.rating?.Id);
        }
    }

    const handleOnFitChange = (newFit) => {
        setFit(newFit[0]);
        form.setValue(reviewFormData?.rating_HowDoesItFit?.Id, newFit[0]);
    }

    const handleOnHeightChange = (e) => {
        const newHeight = e.target.value;
        setSelectedHeight(newHeight);
        form.setValue(reviewFormData?.contextdatavalue_height?.Id, newHeight);
    }

    const handleOnWeightChange = (e) => {
        const newWeight = e.target.value;
        setSelectedWeight(newWeight);
        form.setValue(reviewFormData?.contextdatavalue_weight?.Id, newWeight);
    }

    const handleOnTitleChange = (e) => {
        form.setValue(reviewFormData?.title?.Id, e.target.value);
    };

    const handleOnTextChange = (e) => {
        form.setValue(reviewFormData?.reviewtext?.Id, e.target.value);
    }

    const handleOnNicknameChange = (e) => {
        form.setValue(reviewFormData?.usernickname?.Id, e.target.value);
    }

    const handleOnEmailChange = (e) => {
        form.setValue(reviewFormData?.useremail?.Id, e.target.value);
    }

    const handleOnSizeChange = (e) => {
        const newSize = e.target.value;
        setSelectedSize(newSize);
        form.setValue(reviewFormData?.additionalfield_sizeOrdered?.Id, newSize);
    }

    const { variationAttributes } = useDerivedProduct(product)
    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    const sizeAttribute = variationAttributes.find(attr => attr.id === 'size');
    const sizeOptions = sizeAttribute ? sizeAttribute.values.map(option => ({
        label: option.name,
        value: option.value
    })) : [];

    // Preselect the size if there is only one option
    useEffect(() => {
        if (sizeOptions.length === 1) {
            const singleOption = sizeOptions[0];
            form.setValue(reviewFormData?.additionalfield_sizeOrdered?.Id, singleOption.value);
        }
    }, [sizeOptions, form, reviewFormData]);

    const transformedHeightOptions = reviewFormData?.contextdatavalue_height.Options
        .filter(option => option.Label !== '' && option.Value !== '') // Filter out empty options
        .map(option => ({
            label: option.Label,
            value: option.Value
        }));

    const transformedWeightOptions = reviewFormData?.contextdatavalue_weight.Options
        .filter(option => option.Label !== '' && option.Value !== '') // Filter out empty options
        .map(option => ({
            label: option.Label,
            value: option.Value
        }));

    return (
        <Box>
            {/* Join section */}
            <Box bgColor={'webTheme.backgroundPrimaryBrand'} mt={2} padding={{base: '24px 16px'}}>
                <Text as="h3" textStyle="webH3" mb={4}>
                    {isRegistered ? (
                        <FormattedMessage
                            defaultMessage="LEAVE A REVIEW, GET REWARDED"
                            id="pdp_reviews.join.registered.title"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Join the garage collectif & get rewarded"
                            id="pdp_reviews.join.title"
                        />
                    )}
                </Text>
                <Text textStyle="bodySmRegular">
                    {isRegistered ? (
                        <FormattedMessage
                            defaultMessage="Get"
                            id="pdp_reviews.join.registered.message.part1"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Members get"
                            id="pdp_reviews.join.message.part1"
                        />
                    )}
                    <Text as="span" fontWeight={'700'} px={1}>
                        {isRegistered ? (
                            <FormattedMessage
                                defaultMessage="{number} points"
                                id="pdp_reviews.join.registered.message.part2"
                                values={{number: 10}}
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="{number} points"
                                id="pdp_reviews.join.message.part2"
                                values={{number: 10}}
                            />
                        )}
                    </Text>
                    {isRegistered ? (
                        <FormattedMessage
                            defaultMessage="when you add a review via our website* and"
                            id="pdp_reviews.join.registered.message.part3"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="for every review they leave via our website and another"
                            id="pdp_reviews.join.message.part3"
                        />
                    )}
                    <Text as="span" fontWeight={'700'} px={1}>
                        {isRegistered ? (
                            <FormattedMessage
                                defaultMessage="{number} bonus points"
                                id="pdp_reviews.join.registered.message.part4"
                                values={{number: 10}}
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="{number} bonus points"
                                id="pdp_reviews.join.message.part4"
                                values={{number: 10}}
                            />
                        )}
                    </Text>
                    {isRegistered ? (
                        <FormattedMessage
                            defaultMessage="if you upload a photo, too."
                            id="pdp_reviews.join.registered.message.part5"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="if they upload a photo."
                            id="pdp_reviews.join.message.part5"
                        />
                    )}
                </Text>
                <Text textStyle="bodyXsRegular">
                    <FormattedMessage
                        defaultMessage="*Not available through our app"
                        id="pdp_reviews.join.note"
                    />
                </Text>
                {/* Join link only for guest */}
                {!isRegistered && (
                    <Button
                        variant="link"
                        // textStyle={'ctaOther'}
                        fontSize={{base: '14px'}}
                        fontWeight={'700'}
                        lineHeight={{base: 5}}
                        textTransform={'uppercase'}
                        mt={3}
                        title="Join"
                        onClick={() => onJoinNow?.()}>
                        <FormattedMessage
                            defaultMessage="Join Now"
                            id="pdp_reviews.join.now"
                        />
                    </Button>
                )}
            </Box>

            {/* Form section */}
            <Grid templateColumns={{base: '1fr auto'}} gap={{base: '90px'}} mt={{base: 8}}>
                {/* Rating and Size */}
                <GridItem colSpan={1}>
                    {/* Overall rating */}
                    <Text {...theme.components.WriteReview.fieldTitle}>
                        <FormattedMessage
                            defaultMessage="Overall Rating *"
                            id="pdp_reviews.write.overall"
                        />
                    </Text>
                    <StarRating
                        key={'write-review-rating'}
                        styles={{starWidth: '20', starHeight: '20'}}
                        onRatingChange={handleRatingChange}
                    />

                    {/* I use standard input because the rating is not a standard input */}
                    <input
                        type="hidden"
                        {...form.register(reviewFormData?.rating?.Id, {
                            validate: (value) =>
                                value > 0 || 
                                (isGarage
                                    ? formatMessage({
                                          defaultMessage:
                                              'You must enter a value for overall rating.',
                                          id: 'pdp_reviews.required.rating.garage'
                                      })
                                    : formatMessage({
                                          defaultMessage:
                                              'You must enter a value for overall rating.',
                                          id: 'pdp_reviews.required.rating.dynamite'
                                      }))
                        })}
                        value={rating}
                    />
                    {form.formState.errors[reviewFormData?.rating?.Id] && (
                        <Text
                            role="alert"
                            color="webTypo.textError"
                            mt={2}
                            textStyle={'bodyXsRegular'}
                        >
                            {form.formState.errors[reviewFormData?.rating?.Id].message}
                        </Text>
                    )}

                    {/* Size ordered */}
                    <Text {...theme.components.WriteReview.fieldTitle} mt={8}>
                        <FormattedMessage
                            defaultMessage="Size You Ordered *"
                            id="pdp_reviews.write.size_you_ordered"
                        />
                    </Text>
                    <Field
                        {...fields?.additionalfield_sizeOrdered}
                        label=""
                        placeholder={undefined}
                        hideRequiredIndicator
                        variant="webFloatingField"
                        nativeSelect
                        options={[{label: 'Select *', value: ''}, ...sizeOptions]}
                        inputProps={{sx: {py: '11px', color: 'webTypo.textSecondary'}}}
                        rules={{
                            required: formatMessage({
                                defaultMessage: 'Please select a size.',
                                id: 'pdp_reviews.required.size'
                            })
                        }}
                        error={
                            form.formState.errors[reviewFormData?.additionalfield_sizeOrdered?.Id]
                        }
                        control={form.control}
                        formControlProps={{isDisabled: sizeOptions.length === 1}}
                    />
                </GridItem>

                {/* Product image */}
                <GridItem colSpan={1}>{children}</GridItem>
            </Grid>

            {/* Fit selector */}
            <FitSelector
                onFitChange={handleOnFitChange}
                options={reviewFormData?.rating_HowDoesItFit?.Options}
            />
            {/* I use standard input because the fit is not a standard input */}
            <input
                type="hidden"
                {...form.register(reviewFormData?.rating_HowDoesItFit?.Id)}
                value={fit}
            />

            {/* Share your review */}
            <Text {...theme.components.WriteReview.fieldTitle} mt={8}>
                {isGarage ? (
                    <FormattedMessage
                        defaultMessage="Share your Review"
                        id="pdp_reviews.share.review.garage"
                    />
                ) : (
                    <FormattedMessage
                        defaultMessage="Share your Review"
                        id="pdp_reviews.share.review.dynamite"
                    />
                )}
            </Text>

            <Stack spacing={4}>
                {/* Review title */}
                <Field
                    {...fields?.title}
                    hideLabel={false}
                    labelProps={{...theme.components.WriteReview.fieldTitle}}
                    variant="webFloatingField"
                    inputProps={{
                        onBlur: handleOnTitleChange
                    }}
                />

                {/* Review textarea */}
                <Box>
                    <Box textAlign="right" mb={1}>
                        <Text textStyle="bodyXsRegular" color="webTypo.textSecondary">
                            {reviewTextLength}/{fields?.reviewtext?.rules?.maxLength?.value}
                        </Text>
                    </Box>
                    <Field
                        {...fields?.reviewtext}
                        hideLabel={false}
                        labelProps={{...theme.components.WriteReview.fieldTitle}}
                        type="textarea"
                        variant="webFloatingFieldTextArea"
                        rows={5}
                        inputProps={({onChange}) => ({
                            onChange: (e) => {
                                onChange(e)
                                setReviewTextLength(e.target.value.length)
                            },
                            onBlur: handleOnTextChange
                        })}
                    />
                    {!form.formState.errors[reviewFormData?.reviewtext?.Id] && (
                        <Text textStyle="bodyXsRegular" color="webTypo.textSecondary" mt={1}>
                            <FormattedMessage
                                defaultMessage="Minimum of {min} characters"
                                id="pdp_reviews.min_length.review_text_helper"
                                values={{min: fields?.reviewtext?.rules?.minLength?.value}}
                            />
                        </Text>
                    )}
                </Box>
            </Stack>

            <Text {...theme.components.WriteReview.fieldTitle} mt={8}>
                <FormattedMessage defaultMessage="Add Photos" id="pdp_reviews.add.photos" />
            </Text>

            {/* Image Uploader */}
            <ImageUploader reviewFormData={reviewFormData} form={form} />

            <Text textStyle={'bodySmRegular'} mt={'15px'}>
                {isGarage ? (
                    <FormattedMessage
                        defaultMessage="Add up to 6 photos. Maximum of 10MB per photo."
                        id="pdp_reviews.add.photos.description.garage"
                    />
                ) : (
                    <FormattedMessage
                        defaultMessage="Add up to 6 photos. Maximum of 10MB per photo."
                        id="pdp_reviews.add.photos.description.dynamite"
                    />
                )}
            </Text>

            {/* Your Information */}
            <Text {...theme.components.WriteReview.fieldTitle} mt={8} mb={'15px'}>
                {isGarage ? (
                    <FormattedMessage
                        defaultMessage="Your Information"
                        id="pdp_reviews.your.information.garage"
                    />
                ) : (
                    <FormattedMessage
                        defaultMessage="Your Information"
                        id="pdp_reviews.your.information.dynamite"
                    />
                )}
            </Text>

            <Stack spacing={4}>
                <Field
                    {...fields?.usernickname}
                    hideLabel={false}
                    labelProps={{...theme.components.WriteReview.fieldTitle}}
                    variant="webFloatingField"
                    inputProps={{
                        onBlur: handleOnNicknameChange
                    }}
                />

                <Field
                    {...fields?.useremail}
                    hideLabel={false}
                    labelProps={{...theme.components.WriteReview.fieldTitle}}
                    variant="webFloatingField"
                    inputProps={{
                        onBlur: handleOnEmailChange
                    }}
                />

                <Field
                    {...fields?.contextdatavalue_height}
                    label=""
                    placeholder={undefined}
                    hideRequiredIndicator
                    variant="webFloatingField"
                    nativeSelect
                    options={[{label: 'Height (optional)', value: ''}, ...transformedHeightOptions]}
                    inputProps={{sx: {py: '11px', color: 'webTypo.textSecondary'}}}
                    control={form.control}
                />

                <Field
                    {...fields?.contextdatavalue_weight}
                    label=""
                    placeholder={undefined}
                    hideRequiredIndicator
                    variant="webFloatingField"
                    nativeSelect
                    options={[
                        {label: 'Approx. Weight (optional)', value: ''},
                        ...transformedWeightOptions
                    ]}
                    inputProps={{sx: {py: '11px', color: 'webTypo.textSecondary'}}}
                    control={form.control}
                />
            </Stack>

            <input
                type="hidden"
                {...form.register(reviewFormData?.agreedtotermsandconditions?.Id)}
            />
        </Box>
    )
}

WriteReviewFields.propTypes = {
    reviewFormData: PropTypes.object,
    form: PropTypes.object,
    prefix: PropTypes.string,
    children: PropTypes.node,
    onJoinNow: PropTypes.func
}

export default WriteReviewFields
